// components/RegisterPage.jsx
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    CardTitle,
    Form,
    FormGroup,
    Label,
    Input,
    Button,
    Alert,
    Spinner
} from 'reactstrap';
import { supabase } from '../../lib/supabase';

const RegisterPage = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [alert, setAlert] = useState({ visible: false, message: '', type: '' });
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        confirmPassword: '',
        acceptRules: false
    });

    const showAlert = (message, type = 'info') => {
        setAlert({ visible: true, message, type });
        setTimeout(() => setAlert({ visible: false, message: '', type: '' }), 5000);
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        // Validation
        if (formData.password !== formData.confirmPassword) {
            showAlert('Passwords do not match', 'danger');
            setLoading(false);
            return;
        }

        if (!formData.acceptRules) {
            showAlert('Please accept the rules and terms', 'danger');
            setLoading(false);
            return;
        }

        try {
            // Supabase signup
            const { data, error } = await supabase.auth.signUp({
                email: formData.email,
                password: formData.password,
                options: {
                    data: {
                        full_name: formData.name,
                    }
                }
            });

            if (error) throw error;

            showAlert('Account created successfully! Please check your email for verification.', 'success');

            // Clear form
            setFormData({
                name: '',
                email: '',
                password: '',
                confirmPassword: '',
                acceptRules: false
            });

        } catch (error) {
            showAlert(error.message || 'Failed to create account. Please try again.', 'danger');
        } finally {
            setLoading(false);
        }
    };

    // SVG Pattern as base64 (same as login page for consistency)
    const svgPattern = `
    <svg xmlns="http://www.w3.org/2000/svg" width="400" height="400" viewBox="0 0 400 400">
      <defs>
        <linearGradient id="darkBlueGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stop-color="#0a192f"/>
          <stop offset="100%" stop-color="#112240"/>
        </linearGradient>
        <linearGradient id="orangeGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stop-color="#ff7e5f"/>
          <stop offset="100%" stop-color="#feb47b"/>
        </linearGradient>
        <linearGradient id="silverGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stop-color="#e6e8ec"/>
          <stop offset="100%" stop-color="#f5f7fa"/>
        </linearGradient>
        <pattern id="authPattern" patternUnits="userSpaceOnUse" width="200" height="200" patternTransform="rotate(45)">
          <rect width="200" height="200" fill="url(%23darkBlueGrad)" opacity="0.95"/>
          <circle cx="50" cy="50" r="30" fill="url(%23orangeGrad)" opacity="0.7"/>
          <circle cx="150" cy="150" r="20" fill="url(%23orangeGrad)" opacity="0.5"/>
          <rect x="120" y="20" width="60" height="60" fill="url(%23silverGrad)" opacity="0.3" transform="rotate(45 150 50)"/>
          <rect x="20" y="120" width="60" height="60" fill="url(%23silverGrad)" opacity="0.3" transform="rotate(45 50 150)"/>
          <line x1="0" y1="100" x2="200" y2="100" stroke="url(%23silverGrad)" stroke-width="1" opacity="0.2"/>
          <line x1="100" y1="0" x2="100" y2="200" stroke="url(%23silverGrad)" stroke-width="1" opacity="0.2"/>
          <line x1="0" y1="0" x2="200" y2="200" stroke="url(%23orangeGrad)" stroke-width="2" opacity="0.1"/>
          <line x1="200" y1="0" x2="0" y2="200" stroke="url(%23orangeGrad)" stroke-width="2" opacity="0.1"/>
          <circle cx="25" cy="25" r="2" fill="#f5f7fa" opacity="0.4"/>
          <circle cx="75" cy="75" r="2" fill="#f5f7fa" opacity="0.4"/>
          <circle cx="125" cy="125" r="2" fill="#f5f7fa" opacity="0.4"/>
          <circle cx="175" cy="175" r="2" fill="#f5f7fa" opacity="0.4"/>
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(%23authPattern)"/>
      <rect width="100%" height="100%" fill="url(%23darkBlueGrad)" opacity="0.1"/>
    </svg>
    `;

    const svgDataUrl = `data:image/svg+xml;base64,${btoa(svgPattern)}`;

    return (
        <div className="richads-register" style={styles.pageContainer}>
            {/* Alert */}
            {alert.visible && (
                <Alert color={alert.type} className="fixed-top m-3" style={{ zIndex: 1050 }}>
                    {alert.message}
                </Alert>
            )}

            {/* SVG Pattern Background */}
            <div
                className="auth-pattern-background"
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundImage: `url("${svgDataUrl}")`,
                    backgroundSize: '400px 400px',
                    backgroundPosition: 'center',
                    opacity: 0.9,
                    zIndex: 0
                }}
            />

            {/* Gradient Overlay */}
            <div
                className="gradient-overlay"
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'linear-gradient(135deg, rgba(10, 25, 47, 0.9) 0%, rgba(17, 34, 64, 0.8) 100%)',
                    zIndex: 1
                }}
            />

            {/* Decorative Elements */}
            <div style={styles.decorativeCircle1}></div>
            <div style={styles.decorativeCircle2}></div>
            <div style={styles.decorativeCircle3}></div>

            <Container className="auth-container py-5" style={{ position: 'relative', zIndex: 2 }}>
                <Row className="justify-content-center align-items-center min-vh-100">
                    <Col lg={6} md={8} sm={10}>
                        <Card className="richads-card" style={styles.card}>
                            <CardBody className="p-4">
                                <CardTitle tag="h2" className="text-center fw-bold mb-4">
                                    <div className="py-4 fw-bold mx-auto">
                                        <span className="gradient-text" style={styles.logo}>
                                            <Link to='/' style={{ textDecoration: 'none', color: 'inherit' }}>
                                                Reachtix
                                            </Link>
                                        </span>
                                    </div>
                                    <p className="mt-3" style={{ color: '#feb47b', fontWeight: '500' }}>
                                        Create Your Account
                                    </p>
                                    <p style={{ color: '#e6e8ec', fontSize: '0.9rem', marginTop: '0.5rem' }}>
                                        Join our platform and start your journey
                                    </p>
                                </CardTitle>

                                <Form onSubmit={handleSubmit}>
                                    {/* Name */}
                                    <FormGroup className="mb-3">
                                        <Label for="name" className="fw-bold" style={styles.label}>
                                            <i className="fas fa-user me-2" style={{ color: '#ff7e5f' }}></i>
                                            Full Name
                                        </Label>
                                        <Input
                                            type="text"
                                            name="name"
                                            id="name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            required
                                            placeholder="Enter your full name"
                                            style={styles.input}
                                            className="input-focus"
                                        />
                                    </FormGroup>

                                    {/* Email */}
                                    <FormGroup className="mb-3">
                                        <Label for="email" className="fw-bold" style={styles.label}>
                                            <i className="fas fa-envelope me-2" style={{ color: '#ff7e5f' }}></i>
                                            Email Address
                                        </Label>
                                        <Input
                                            type="email"
                                            name="email"
                                            id="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            required
                                            placeholder="Enter your email"
                                            style={styles.input}
                                            className="input-focus"
                                        />
                                    </FormGroup>

                                    {/* Password */}
                                    <FormGroup className="mb-3">
                                        <Label for="password" className="fw-bold" style={styles.label}>
                                            <i className="fas fa-lock me-2" style={{ color: '#ff7e5f' }}></i>
                                            Password
                                        </Label>
                                        <Input
                                            type="password"
                                            name="password"
                                            id="password"
                                            value={formData.password}
                                            onChange={handleChange}
                                            required
                                            minLength={6}
                                            placeholder="Create a password (min. 6 characters)"
                                            style={styles.input}
                                            className="input-focus"
                                        />
                                    </FormGroup>

                                    {/* Confirm Password */}
                                    <FormGroup className="mb-4">
                                        <Label for="confirmPassword" className="fw-bold" style={styles.label}>
                                            <i className="fas fa-lock me-2" style={{ color: '#ff7e5f' }}></i>
                                            Confirm Password
                                        </Label>
                                        <Input
                                            type="password"
                                            name="confirmPassword"
                                            id="confirmPassword"
                                            value={formData.confirmPassword}
                                            onChange={handleChange}
                                            required
                                            placeholder="Confirm your password"
                                            style={styles.input}
                                            className="input-focus"
                                        />
                                    </FormGroup>

                                    {/* Password Requirements */}
                                    <div className="mb-4 p-3" style={styles.passwordRequirements}>
                                        <p style={{ color: '#e6e8ec', fontSize: '0.8rem', marginBottom: '0.5rem' }}>
                                            <i className="fas fa-info-circle me-2" style={{ color: '#ff7e5f' }}></i>
                                            Password requirements:
                                        </p>
                                        <ul style={{
                                            color: '#e6e8ec',
                                            fontSize: '0.75rem',
                                            paddingLeft: '1.5rem',
                                            marginBottom: 0
                                        }}>
                                            <li>Minimum 6 characters</li>
                                            <li>Use a combination of letters and numbers</li>
                                            <li>Avoid common passwords</li>
                                        </ul>
                                    </div>

                                    {/* Rules Acceptance */}
                                    <FormGroup check className="mb-4">
                                        <Input
                                            type="checkbox"
                                            name="acceptRules"
                                            id="acceptRules"
                                            checked={formData.acceptRules}
                                            onChange={handleChange}
                                            required
                                            style={{ cursor: 'pointer' }}
                                        />
                                        <Label check for="acceptRules" className="ms-2" style={{ color: '#e6e8ec' }}>
                                            I agree to the{' '}
                                            <Link
                                                to="/terms"
                                                target="_blank"
                                                style={{ color: '#ff7e5f', fontWeight: '500' }}
                                            >
                                                Terms of Service
                                            </Link>{' '}
                                            and{' '}
                                            <Link
                                                to="/privacy"
                                                target="_blank"
                                                style={{ color: '#ff7e5f', fontWeight: '500' }}
                                            >
                                                Privacy Policy
                                            </Link>
                                        </Label>
                                    </FormGroup>

                                    {/* Submit Button */}
                                    <Button
                                        color="primary"
                                        type="submit"
                                        disabled={loading}
                                        className="w-100 btn-primary py-2"
                                        size="lg"
                                        style={styles.button}
                                    >
                                        {loading ? (
                                            <>
                                                <Spinner size="sm" className="me-2" />
                                                Creating Account...
                                            </>
                                        ) : (
                                            <>
                                                <i className="fas fa-user-plus me-2"></i>
                                                Create Account
                                            </>
                                        )}
                                    </Button>
                                </Form>

                                <div className="text-center mt-4">
                                    <small style={{ color: '#e6e8ec' }}>
                                        Already have an account?{' '}
                                        <Link
                                            to="/login"
                                            style={{
                                                color: '#ff7e5f',
                                                fontWeight: 'bold',
                                                textDecoration: 'none'
                                            }}
                                        >
                                            Sign in here
                                        </Link>
                                    </small>
                                </div>

                                {/* Social Signup (Optional) */}
                                <div className="mt-4">
                                    <div className="text-center" style={{ color: '#e6e8ec', marginBottom: '1rem' }}>
                                        <span style={{ fontSize: '0.9rem' }}>Or sign up with</span>
                                    </div>
                                    <div className="d-flex justify-content-center gap-3">
                                        <Button
                                            outline
                                            style={styles.socialButton}
                                            disabled
                                        >
                                            <i className="fab fa-google" style={{ color: '#ff7e5f' }}></i>
                                        </Button>
                                        <Button
                                            outline
                                            style={styles.socialButton}
                                            disabled
                                        >
                                            <i className="fab fa-github" style={{ color: '#ff7e5f' }}></i>
                                        </Button>
                                        <Button
                                            outline
                                            style={styles.socialButton}
                                            disabled
                                        >
                                            <i className="fab fa-linkedin" style={{ color: '#ff7e5f' }}></i>
                                        </Button>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

// Inline Styles
const styles = {
    pageContainer: {
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden'
    },
    card: {
        background: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: '16px',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
        transition: 'transform 0.3s ease, box-shadow 0.3s ease'
    },
    logo: {
        background: 'linear-gradient(90deg, #ff7e5f 0%, #feb47b 100%)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
        fontWeight: '800',
        fontSize: '2.5rem',
        letterSpacing: '1px'
    },
    label: {
        color: '#f5f7fa',
        marginBottom: '0.5rem',
        fontSize: '0.9rem',
        letterSpacing: '0.5px'
    },
    input: {
        backgroundColor: 'rgba(230, 232, 236, 0.1)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        color: '#f5f7fa',
        borderRadius: '8px',
        padding: '12px 16px',
        fontSize: '0.95rem',
        transition: 'all 0.3s ease'
    },
    button: {
        background: 'linear-gradient(90deg, #ff7e5f 0%, #feb47b 100%)',
        border: 'none',
        borderRadius: '8px',
        fontWeight: '600',
        letterSpacing: '0.5px',
        transition: 'all 0.3s ease',
        padding: '14px',
        fontSize: '1rem'
    },
    passwordRequirements: {
        background: 'rgba(10, 25, 47, 0.3)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: '8px',
        borderLeft: '3px solid #ff7e5f'
    },
    socialButton: {
        width: '50px',
        height: '50px',
        borderRadius: '50%',
        borderColor: 'rgba(255, 255, 255, 0.2)',
        color: '#f5f7fa',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.3s ease'
    },
    decorativeCircle1: {
        position: 'fixed',
        top: '10%',
        left: '10%',
        width: '150px',
        height: '150px',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(255, 126, 95, 0.15) 0%, transparent 70%)',
        zIndex: 1,
        pointerEvents: 'none'
    },
    decorativeCircle2: {
        position: 'fixed',
        bottom: '15%',
        right: '10%',
        width: '200px',
        height: '200px',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(254, 180, 123, 0.1) 0%, transparent 70%)',
        zIndex: 1,
        pointerEvents: 'none'
    },
    decorativeCircle3: {
        position: 'fixed',
        top: '50%',
        left: '80%',
        width: '100px',
        height: '100px',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(230, 232, 236, 0.05) 0%, transparent 70%)',
        zIndex: 1,
        pointerEvents: 'none'
    }
};

// Add hover effects via CSS
const additionalStyles = `
    <style>
        .richads-card:hover {
            transform: translateY(-5px);
            box-shadow: 0 25px 80px rgba(0, 0, 0, 0.4);
        }
        
        .input-focus:focus {
            background-color: rgba(230, 232, 236, 0.15) !important;
            border-color: #ff7e5f !important;
            box-shadow: 0 0 0 0.2rem rgba(255, 126, 95, 0.25) !important;
            color: #f5f7fa !important;
        }
        
        .btn-primary:hover {
            transform: translateY(-2px);
            box-shadow: 0 10px 25px rgba(255, 126, 95, 0.4) !important;
        }
        
        .btn-primary:disabled {
            opacity: 0.6;
            cursor: not-allowed;
        }
        
        .social-button:hover {
            background-color: rgba(255, 126, 95, 0.1) !important;
            border-color: #ff7e5f !important;
            transform: translateY(-2px);
        }
        
        a:hover {
            opacity: 0.8;
        }
    </style>
`;

export default RegisterPage;